import { createSelector, Selector } from '@reduxjs/toolkit'
import { groupBy } from 'lodash-es'
import { GlobalState } from 'store'
import { DateFormat, Direction, OrderDetail, OrderDetailSummary, SummaryRowDirection } from 'api'
import { createStagingOrderDetailsSelector } from './orderSelectors'
import { add, subtract } from 'utils/Decimal'
import { aggregateAllOrNothing, Decimal } from 'utils'
import { Locale } from 'locale'
import { isAonOrOcoOrSmartGroupCollection } from 'model'

export type StagingGroupRowData = Record<string, OrderDetailSummary>
export type StagingGroupContainerCollectionRowData = Record<string, OrderDetailSummary>

export function createStagingOrdersCollectionGroupRowDataSelector(): Selector<GlobalState, StagingGroupRowData | null> {
  return createSelector(createStagingOrderDetailsSelector(), stagingOrderDetails => {
    const someOrdersAreInCollection = stagingOrderDetails.some(
      orderDetail => orderDetail.collection && isAonOrOcoOrSmartGroupCollection(orderDetail.collection.type)
    )

    if (!someOrdersAreInCollection) {
      return null
    }

    // Group those orders by their collection id
    const collectionOrdersGroupedByCollectionId = groupBy(
      stagingOrderDetails.filter(orderDetail => orderDetail.collection),
      orderDetail => orderDetail.collection?.id
    )

    // Aggregate
    const collectionGroupRowData = Object.keys(collectionOrdersGroupedByCollectionId).reduce((acc, collectionId) => {
      acc[collectionId] = aggregateOrderDetailCollection(collectionOrdersGroupedByCollectionId[collectionId])
      return acc
    }, {} as Record<string, OrderDetailSummary>)

    return collectionGroupRowData
  })
}

export function createStagingOrdersCollectionContainerGroupRowData(): Selector<GlobalState, StagingGroupContainerCollectionRowData | null> {
  return createSelector(createStagingOrderDetailsSelector(), stagingOrderDetails => {
    const someOrdersAreInContainer = stagingOrderDetails.some(orderDetail => !!orderDetail.containerCollection)

    if (!someOrdersAreInContainer) return null

    // Group those orders by their container collection id
    const containerCollectionOrdersGroupedByCollectionId = groupBy(
      stagingOrderDetails.filter(orderDetail => orderDetail.containerCollection?.id),
      orderDetail => orderDetail.containerCollection?.id
    )

    // Aggregate
    const collectionGroupRowData = Object.keys(containerCollectionOrdersGroupedByCollectionId).reduce<
      StagingGroupContainerCollectionRowData
    >((acc, containerCollectionId) => {
      const orderDetails = containerCollectionOrdersGroupedByCollectionId[containerCollectionId]

      acc[containerCollectionId] = {
        ...emptyOrderDetailCollection(),
        collection: {
          type: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.containerCollection?.type),
          name: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.containerCollection?.name),
          id: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.containerCollection?.id)
        },
        containerCollection: {
          name: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.containerCollection?.name),
          id: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.containerCollection?.id)
        }
      }

      return acc
    }, {})

    return collectionGroupRowData
  })
}

function aggregateAllOrNothingCreatedAtDateTime(orderDetails: OrderDetail[]) {
  const firstChildDate = orderDetails[0].order.createdAt
  if (
    orderDetails.every(
      orderDetail => Locale.formatDate(orderDetail.order.createdAt, DateFormat.EU) === Locale.formatDate(firstChildDate, DateFormat.EU)
    )
  ) {
    if (orderDetails.every(orderDetail => Locale.formatTime(orderDetail.order.createdAt) === Locale.formatTime(firstChildDate))) {
      return { createdAtDate: firstChildDate, createdAtTime: firstChildDate }
    }
    return { createdAtDate: firstChildDate }
  }

  return undefined
}

export const aggregateOrderDetailCollection = (orderDetails: OrderDetail[]): OrderDetailSummary => {
  const collateral = aggregateCollateral(orderDetails).collateral
  const order = aggregateOrder(orderDetails).order
  return {
    order,
    collection: {
      status: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.collection?.status),
      name: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.collection?.name),
      type: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.collection?.type),
      id: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.collection?.id)
    },
    containerCollection: {
      name: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.containerCollection?.name),
      id: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.containerCollection?.id)
    },
    collateral,
    collateralBasket: {
      name: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.collateralBasket?.name),
      description: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.collateralBasket?.description),
      dbvClass: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.collateralBasket?.dbvClass),
      collateralBasketIsin: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.collateralBasket?.collateralBasketIsin)
    },
    productSector: {
      name: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.productSector?.name),
      basketType: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.productSector?.basketType),
      basketCurrency: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.productSector?.basketCurrency)
    },
    product: {
      name: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.product?.name)
    },
    orderFund: {
      publishedName: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.orderFund?.publishedName),
      lei: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.orderFund?.lei),
      id: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.orderFund?.id)
    },
    primeBroker: {
      name: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.primeBroker?.name)
    },
    referenceRate: {
      name: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.referenceRate?.name)
    },
    repoType: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.order.repoType) ?? null,
    floatCrystallisationDays: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.floatCrystallisationDays) ?? null,
    investmentDecisionMaker: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.investmentDecisionMaker) ?? null,
    executionDecisionMaker: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.executionDecisionMaker) ?? null,
    counterparty: {
      alias: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.counterparty?.alias),
      publishedName: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.counterparty?.publishedName)
    },
    secondaryInstrumentName: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.secondaryInstrumentName) ?? null,
    clearingHouseName: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.clearingHouseName) ?? null,
    sponsoringBankName: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.sponsoringBankName) ?? null,
    linkedTradeId: null
  }
}

const aggregateOrderHaircutAvg = (orderDetails: OrderDetail[], valueGetter: (value: OrderDetail) => number | null): number | null => {
  if (orderDetails.every(({ order }) => order.haircut === null)) {
    return null
  }

  if (aggregateOrderDirection(orderDetails, orderDetail => orderDetail.order.direction) === SummaryRowDirection.MULTIPLE) {
    return null
  }

  return Decimal.unsafeDivide(
    orderDetails.reduce((acc: number, orderDetail) => {
      const value = valueGetter(orderDetail)
      if (!value) {
        return acc
      }
      return Decimal.add(acc, value)
    }, 0),
    orderDetails.length
  )
}

const aggregateOrderAbsolute = (orderDetails: OrderDetail[], valueGetter: (value: OrderDetail) => number | null): number | null => {
  return orderDetails.reduce((acc: number, orderDetail) => {
    const value = valueGetter(orderDetail)
    if (!value) {
      return acc
    }
    return add(acc, value)
  }, 0)
}

const aggregateOrderBuySell = (orderDetails: OrderDetail[], valueGetter: (value: OrderDetail) => number | null): number | null => {
  return orderDetails.reduce((acc: number, orderDetail) => {
    const direction = orderDetail.order.direction
    const value = valueGetter(orderDetail)

    if (!direction || !value) {
      return acc
    }

    const sumFn = direction === Direction.BUY ? add : subtract
    return sumFn(acc, value)
  }, 0)
}

const aggregateOrderDirection = (
  orderDetails: OrderDetail[],
  valueGetter: (value: OrderDetail) => Direction | null
): Direction | SummaryRowDirection | null => {
  const ordersWithDirection = orderDetails.filter(orderDetail => valueGetter(orderDetail))

  if (ordersWithDirection.length === 0) {
    return null
  }

  const firstOrderDirection = valueGetter(ordersWithDirection[0])

  if (!firstOrderDirection) {
    return null
  }

  if (ordersWithDirection.every(orderDetail => valueGetter(orderDetail) === firstOrderDirection)) {
    return firstOrderDirection
  }

  return SummaryRowDirection.MULTIPLE
}

const aggregateCollateral = (orderDetails: OrderDetail[]): Pick<OrderDetailSummary, 'collateral'> => {
  return {
    collateral: {
      currency: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.collateral?.currency),
      name: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.collateral?.name),
      issuer: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.collateral?.issuer),
      id: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.collateral?.id),
      isin: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.collateral?.isin),
      cusip: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.collateral?.cusip)
    }
  }
}

const aggregateOrder = (orderDetails: OrderDetail[]): Pick<OrderDetailSummary, 'order'> => {
  const currency = aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.order.currency)
  const currencyDependentOrderFields = {
    notional: currency ? aggregateOrderBuySell(orderDetails, orderDetail => orderDetail.order.notional) : null,
    accruedInterest: currency ? aggregateOrderBuySell(orderDetails, orderDetail => orderDetail.order.accruedInterest) : null,
    startConsideration: currency ? aggregateOrderBuySell(orderDetails, orderDetail => orderDetail.order.startConsideration) : null,
    absoluteConsideration: currency ? aggregateOrderAbsolute(orderDetails, orderDetail => orderDetail.order.startConsideration) : null
  }

  const createdAtDateTime = aggregateAllOrNothingCreatedAtDateTime(orderDetails)

  return {
    order: {
      basketAllocation: {
        basketId: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.order?.basketAllocation?.basketId)
      },
      evergreenNoticePeriod: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.order.evergreenNoticePeriod),
      marketIdentifierCode: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.order.marketIdentifierCode),
      marketTradeDate: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.order.marketTradeDate),
      collateralId: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.order.collateralId),
      status: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.order.status),
      direction: aggregateOrderDirection(orderDetails, orderDetail => orderDetail.order.direction),
      fundId: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.order.fundId),
      term: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.order.term),
      startDate: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.order.startDate),
      endDate: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.order.endDate),
      repoType: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.order.repoType),
      repoClassification: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.order.repoClassification),
      createdBy: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.order.createdBy),
      createdAt: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.order.createdAt),
      lastUpdatedBy: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.order.lastUpdatedBy),
      referenceRate: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.order.referenceRate),
      externalOrderId: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.order.externalOrderId),
      positionId: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.order.positionId),
      callableType: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.order.callableType),
      callablePeriodDays: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.order.callablePeriodDays),
      noticePeriodDays: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.order.noticePeriodDays),
      book: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.order.book),
      bundle: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.order.bundle),
      strategy: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.order.strategy),
      currency: currency,
      market: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.order.market),
      blockTrade: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.order.blockTrade),
      rfqType: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.order.rfqType),
      allowPartial: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.order.allowPartial),
      preAgreed: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.order.preAgreed),

      negotiationInstruction: {
        ...orderDetails[0].order.negotiationInstruction,
        csd: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.order.negotiationInstruction.csd, null)
      },
      notes: null,
      uploadRowIndex: null,
      haircut: aggregateOrderHaircutAvg(orderDetails, orderDetail => orderDetail.order.haircut),
      haircutType: aggregateAllOrNothing(orderDetails, orderDetail => orderDetail.order.haircutType),
      ...createdAtDateTime,
      ...currencyDependentOrderFields
    }
  }
}

export const emptyOrderDetailCollection = (): OrderDetailSummary => {
  return {
    order: {
      notional: null,
      accruedInterest: null,
      startConsideration: null,
      absoluteConsideration: null,
      basketAllocation: {
        basketId: undefined
      },
      collateralId: undefined,
      status: undefined,
      direction: null,
      fundId: undefined,
      term: undefined,
      startDate: undefined,
      endDate: undefined,
      repoType: undefined,
      repoClassification: undefined,
      createdBy: undefined,
      createdAt: undefined,
      lastUpdatedBy: undefined,
      referenceRate: undefined,
      externalOrderId: undefined,
      positionId: undefined,
      callableType: undefined,
      callablePeriodDays: undefined,
      noticePeriodDays: undefined,
      book: undefined,
      bundle: undefined,
      strategy: undefined,
      currency: undefined,
      market: undefined,
      blockTrade: undefined,
      rfqType: undefined,
      allowPartial: undefined,
      preAgreed: undefined,
      negotiationInstruction: undefined,
      notes: null,
      uploadRowIndex: null
    },
    collection: {
      status: undefined,
      name: undefined,
      type: undefined,
      id: undefined
    },
    containerCollection: {
      name: undefined,
      id: undefined
    },
    collateral: {
      currency: undefined,
      name: undefined,
      issuer: undefined,
      id: undefined,
      isin: undefined,
      cusip: undefined
    },
    collateralBasket: {
      name: undefined,
      description: undefined,
      dbvClass: undefined,
      collateralBasketIsin: undefined
    },
    productSector: {
      name: undefined,
      basketType: undefined,
      basketCurrency: undefined
    },
    product: {
      name: undefined
    },
    orderFund: {
      publishedName: undefined,
      lei: undefined,
      id: undefined
    },
    primeBroker: {
      name: undefined
    },
    referenceRate: {
      name: undefined
    },
    repoType: null,
    floatCrystallisationDays: null,
    investmentDecisionMaker: null,
    executionDecisionMaker: null,
    counterparty: {
      alias: undefined,
      publishedName: undefined
    },
    secondaryInstrumentName: null,
    clearingHouseName: null,
    sponsoringBankName: null,
    linkedTradeId: null
  }
}
